import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Search, Filter, ChevronRight, X, Copy, ExternalLink,
  Loader2, RefreshCw, Truck, ArrowLeft, ShieldCheck, User,
  Clock, MapPin, Code2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { adminFetch } from '../lib/adminFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  CREATED:            { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Registrato' },
  LABEL_CREATED:      { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Etichetta' },
  PICKED_UP:          { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Ritirato' },
  IN_TRANSIT:         { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In transito' },
  OUT_FOR_DELIVERY:   { bg: 'bg-amber-100', text: 'text-amber-800', label: 'In consegna' },
  DELIVERY_ATTEMPTED: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Tentata' },
  DELIVERED:          { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Consegnato' },
  EXCEPTION:          { bg: 'bg-red-100', text: 'text-red-800', label: 'Anomalia' },
  LOST:               { bg: 'bg-red-100', text: 'text-red-900', label: 'Smarrito' },
  DAMAGED:            { bg: 'bg-red-100', text: 'text-red-800', label: 'Danneggiato' },
  RETURN_TO_SENDER:   { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Restituito' },
  UNKNOWN:            { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Sconosciuto' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const copyToClipboard = (text) => {
  navigator.clipboard?.writeText(text);
  toast.success('Copiato');
};

/**
 * AdminShipmentsPage — vista amministrativa dei shipment Rarifex.
 * Tabella filtered + detail drawer con timeline e raw payload per debug.
 */
export default function AdminShipmentsPage() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '200');
      const r = await adminFetch(`${API_URL}/api/admin/shipments?${params.toString()}`);
      if (!r.ok) throw new Error('load failed');
      const data = await r.json();
      setShipments(Array.isArray(data?.shipments) ? data.shipments : []);
    } catch (e) {
      toast.error('Errore caricamento shipments');
      setShipments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return shipments;
    return shipments.filter((s) =>
      [s.tracking_number, s.carrier_name, s.seller_name, s.client_name, s.order_number, s.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [shipments, search]);

  const statusOptions = ['', 'CREATED', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED', 'EXCEPTION', 'RETURN_TO_SENDER', 'UNKNOWN'];

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> SuperAdmin
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
              <Package className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-slate-900">Shipments</h1>
            <Badge variant="outline" className="text-xs">{shipments.length}</Badge>
          </div>
          <div className="ml-auto">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca tracking, corriere, venditore, cliente, ordine..."
              className="pl-10"
              data-testid="admin-search-input"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 pl-10 pr-8 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="admin-status-filter"
            >
              {statusOptions.map((s) => (
                <option key={s || 'all'} value={s}>{s ? (STATUS_COLORS[s]?.label || s) : 'Tutti gli stati'}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Caricamento...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nessuna spedizione trovata</p>
              <p className="text-xs mt-1">Prova a rimuovere i filtri o attendi le prime spedizioni</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Ordine</th>
                    <th className="text-left px-4 py-3 font-semibold">Venditore</th>
                    <th className="text-left px-4 py-3 font-semibold">Corriere</th>
                    <th className="text-left px-4 py-3 font-semibold">Tracking</th>
                    <th className="text-left px-4 py-3 font-semibold">Provider</th>
                    <th className="text-left px-4 py-3 font-semibold">Stato</th>
                    <th className="text-left px-4 py-3 font-semibold">Spedito</th>
                    <th className="text-left px-4 py-3 font-semibold">Consegnato</th>
                    <th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((s) => {
                    const sc = STATUS_COLORS[s.status] || STATUS_COLORS.UNKNOWN;
                    return (
                      <tr
                        key={s.id}
                        onClick={() => setSelectedId(s.id)}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                        data-testid={`admin-shipment-row-${s.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs text-slate-700">{s.order_number || s.order_id?.slice(0, 8)}</div>
                          {s.client_name && <div className="text-[11px] text-slate-400 mt-0.5 truncate max-w-[160px]">{s.client_name}</div>}
                        </td>
                        <td className="px-4 py-3 truncate max-w-[180px]">{s.seller_name}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{s.carrier_name}</td>
                        <td className="px-4 py-3 font-mono text-xs">{s.tracking_number || '—'}</td>
                        <td className="px-4 py-3">
                          {s.tracking_provider_type === 'INTEGRATED' ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">AUTO</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">MANUAL</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${sc.bg} ${sc.text}`}>
                            {sc.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{fmt(s.shipped_at)}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{fmt(s.delivered_at)}</td>
                        <td className="px-4 py-3 text-slate-300">
                          <ChevronRight className="w-4 h-4" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ShipmentDetailDialog id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/**
 * Modal dettaglio con timeline completa + raw payload per ogni evento (admin only).
 */
const ShipmentDetailDialog = ({ id, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRaw, setExpandedRaw] = useState({});

  useEffect(() => {
    if (!id) { setData(null); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await adminFetch(`${API_URL}/api/admin/shipments/${id}`);
        if (!r.ok) throw new Error();
        setData(await r.json());
      } catch {
        toast.error('Errore caricamento dettaglio');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [id, onClose]);

  if (!id) return null;
  const s = data?.shipment;

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="admin-shipment-detail">
        {loading || !s ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-white text-xl flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Shipment {s.id.slice(0, 8)}
                  <Button variant="ghost" size="icon" className="ml-1 text-slate-300 hover:text-white hover:bg-white/10" onClick={() => copyToClipboard(s.id)}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
                <InfoTile label="Ordine" value={data.order?.order_number || s.order_id?.slice(0, 8)} />
                <InfoTile label="Venditore" value={data.seller?.storefront_title || data.seller?.full_name || '—'} icon={<User className="w-3 h-3" />} />
                <InfoTile label="Cliente" value={data.client?.full_name || data.client?.email || '—'} />
                <InfoTile label="Stato" value={
                  <span className={`inline-block px-2 py-0.5 rounded ${STATUS_COLORS[s.status]?.bg || 'bg-slate-200'} ${STATUS_COLORS[s.status]?.text || 'text-slate-700'}`}>
                    {STATUS_COLORS[s.status]?.label || s.status}
                  </span>
                } />
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Info corriere */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MetaBox label="Corriere" value={s.carrier_name} />
                <MetaBox
                  label="Tracking"
                  value={s.tracking_number || '—'}
                  copyable={!!s.tracking_number}
                  onCopy={() => copyToClipboard(s.tracking_number)}
                />
                <MetaBox
                  label="Provider"
                  value={s.tracking_provider_type === 'INTEGRATED' ? 'Automatico' : 'Manuale'}
                  badge={s.tracking_provider_type === 'INTEGRATED' ? { label: 'AUTO', color: 'bg-emerald-500' } : null}
                />
                {s.tracking_url && (
                  <a
                    href={s.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sm:col-span-3 flex items-center justify-between px-4 py-3 rounded-lg bg-teal-50 border border-teal-200 hover:bg-teal-100 group"
                  >
                    <span className="text-xs text-teal-800 font-medium truncate">{s.tracking_url}</span>
                    <ExternalLink className="w-4 h-4 text-teal-700 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                )}
                <MetaBox label="Spedito" value={fmt(s.shipped_at)} />
                <MetaBox label="Prima scansione" value={fmt(s.first_scan_at)} />
                <MetaBox label="Consegnato" value={fmt(s.delivered_at)} />
              </div>

              {/* Timeline eventi */}
              <div>
                <h4 className="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Timeline eventi ({data.events?.length || 0})
                </h4>
                {(!data.events || data.events.length === 0) ? (
                  <div className="text-xs text-slate-400 p-4 border border-dashed border-slate-200 rounded-lg text-center">
                    Nessun evento registrato
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.events.map((e, i) => {
                      const meta = STATUS_COLORS[e.event_type] || STATUS_COLORS.UNKNOWN;
                      const isExpanded = expandedRaw[e.id];
                      return (
                        <motion.div
                          key={e.id}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className="border border-slate-200 rounded-lg overflow-hidden bg-white"
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedRaw((prev) => ({ ...prev, [e.id]: !prev[e.id] }))}
                            className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3"
                          >
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${meta.bg} ${meta.text}`}>
                              {meta.label}
                            </span>
                            {e.description && <span className="text-xs text-slate-700 truncate">{e.description}</span>}
                            {e.location && <span className="text-[11px] text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                            <span className="ml-auto text-[11px] text-slate-400">{fmt(e.event_timestamp)}</span>
                            <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="p-3 bg-slate-900 border-t border-slate-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-slate-400 tracking-widest">
                                      <Code2 className="w-3 h-3" /> Raw Payload · source={e.source} · carrier_status={e.carrier_status || '—'}
                                    </div>
                                    {e.raw_payload && (
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(JSON.stringify(e.raw_payload, null, 2))}
                                        className="text-[10px] text-slate-400 hover:text-white flex items-center gap-1"
                                      >
                                        <Copy className="w-3 h-3" /> Copia JSON
                                      </button>
                                    )}
                                  </div>
                                  <pre className="text-[11px] text-emerald-200 font-mono whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
{e.raw_payload ? JSON.stringify(e.raw_payload, null, 2) : '(nessun raw payload salvato)'}
                                  </pre>
                                  <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-slate-400">
                                    <div>event_id: <span className="text-slate-200 font-mono">{e.id}</span></div>
                                    <div>received_at: <span className="text-slate-200">{fmt(e.received_at)}</span></div>
                                    <div>event_type: <span className="text-emerald-300 font-mono">{e.event_type}</span></div>
                                    <div>dedup_key: <span className="text-slate-200 font-mono">{e.dedup_key || '—'}</span></div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InfoTile = ({ label, value, icon }) => (
  <div>
    <div className="text-slate-400 text-[10px] uppercase tracking-wide flex items-center gap-1">{icon}{label}</div>
    <div className="mt-0.5 font-medium truncate">{value}</div>
  </div>
);

const MetaBox = ({ label, value, badge, copyable, onCopy }) => (
  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
    <div className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center justify-between">
      <span>{label}</span>
      {badge && <span className={`text-white ${badge.color} px-1.5 py-0.5 rounded text-[9px] font-bold`}>{badge.label}</span>}
    </div>
    <div className="mt-1 flex items-center gap-2">
      <div className="text-sm font-medium text-slate-900 truncate">{value}</div>
      {copyable && (
        <button onClick={onCopy} className="text-slate-400 hover:text-slate-700">
          <Copy className="w-3 h-3" />
        </button>
      )}
    </div>
  </div>
);
